<template>
  <div class="h-full bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-3xl p-5 sm:p-6 shadow-sm hover:shadow-xl hover:border-amber-500/40 transition-all duration-300 flex flex-col justify-between relative group select-none">
    <!-- Top Accent Glow on Hover -->
    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>

    <div>
      <!-- Rating Stars & Verified Badge -->
      <div class="flex items-center justify-between gap-2 mb-3">
        <div class="flex items-center gap-1">
          <span
            v-for="star in 5"
            :key="star"
            class="text-amber-400 text-base leading-none"
          >
            ★
          </span>
          <span class="text-xs font-black text-amber-500 ml-1">5.0</span>
        </div>

        <span class="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
          Verified Buyer
        </span>
      </div>

      <!-- Product Tag (if review is tied to a specific product) -->
      <div v-if="testimonial.product_name" class="mb-3">
        <component
          :is="testimonial.product_url ? 'a' : 'div'"
          :href="testimonial.product_url || undefined"
          :target="testimonial.product_url ? '_blank' : undefined"
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold hover:text-amber-600 dark:hover:text-amber-400 transition-colors max-w-full truncate"
          title="Product reviewed"
        >
          <img
            v-if="testimonial.product_image"
            :src="testimonial.product_image"
            alt=""
            class="w-4 h-4 rounded-md object-cover shrink-0"
            @error="onProductImgError"
          />
          <span class="truncate">{{ testimonial.product_name }}</span>
          <span v-if="testimonial.product_url" class="text-[10px] text-slate-400">↗</span>
        </component>
      </div>

      <!-- Testimonial Text -->
      <p class="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-4 italic">
        "{{ cleanComment }}"
      </p>
    </div>

    <!-- Author & Date Footer -->
    <div class="flex items-center justify-between pt-3.5 border-t border-slate-100 dark:border-slate-800/80 mt-auto">
      <div class="flex items-center gap-2.5 min-w-0">
        <!-- Avatar or Initials -->
        <div
          class="w-9 h-9 rounded-full flex items-center justify-center font-black text-xs text-white shrink-0 shadow-sm"
          :class="avatarBgClass"
        >
          {{ authorInitials }}
        </div>

        <div class="min-w-0">
          <h4 class="font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white truncate">
            {{ authorDisplayName }}
          </h4>
        </div>
      </div>

      <div class="text-amber-500/20 text-3xl font-serif leading-none shrink-0 select-none">
        ”
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'TestimonialCard',
  props: {
    testimonial: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const cleanComment = computed(() => {
      const text = props.testimonial.comment || props.testimonial.text;
      if (!text || !text.trim()) {
        return 'Outstanding quality and super fast service! Highly recommended platform.';
      }
      return text.trim();
    });

    // Display ONLY the first name (e.g. 'aboura sidou' -> 'aboura', 'novastore' -> 'novastore')
    const authorDisplayName = computed(() => {
      if (props.testimonial.is_anonymous) {
        return 'Anonymous';
      }
      const fullName = (props.testimonial.author_name || props.testimonial.name || '').trim();
      if (!fullName) return 'Client';
      const parts = fullName.split(/\s+/);
      return parts[0];
    });

    const authorInitials = computed(() => {
      const name = authorDisplayName.value.trim();
      if (!name) return 'C';
      return name.slice(0, 2).toUpperCase();
    });

    // Deterministic colorful background gradient based on author name
    const avatarBgClass = computed(() => {
      const gradients = [
        'bg-gradient-to-br from-amber-500 to-orange-600',
        'bg-gradient-to-br from-blue-500 to-indigo-600',
        'bg-gradient-to-br from-purple-500 to-pink-600',
        'bg-gradient-to-br from-emerald-500 to-teal-600',
        'bg-gradient-to-br from-rose-500 to-red-600'
      ];
      let hash = 0;
      const str = authorDisplayName.value;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % gradients.length;
      return gradients[index];
    });

    const onProductImgError = (e) => {
      e.target.style.display = 'none';
    };

    return {
      cleanComment,
      authorDisplayName,
      authorInitials,
      avatarBgClass,
      onProductImgError
    };
  }
};
</script>